import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, User as UserIcon, Link2 } from 'lucide-react';
import userService, { CreateUserRequest, UpdateUserRequest, User, UserRole } from '../services/userService';
import { alertService } from '../services/alertService';
import companyService from '../services/companyService';
import { EmpresaResponseDTO } from '../types/company';
import CompanyLinkModal from '../components/users/CompanyLinkModal';

const UserInfoPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = id === undefined;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availableRoles, setAvailableRoles] = useState<UserRole[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [userData, setUserData] = useState<User | null>(null);

  const [form, setForm] = useState({
    username: '',
    nome: '',
    sobrenome: '',
    email: '',
    telefone: '',
    emailVerificado: false,
    roles: ['OPERADOR'] as UserRole[],
    ativo: true,
    password: ''
  });

  const [linkedCompanies, setLinkedCompanies] = useState<EmpresaResponseDTO[]>([]);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const loadRoles = async () => {
    try {
      const res = await userService.getRoles();
      if (res.success) setAvailableRoles(res.data);
    } catch (e) { console.error(e); }
  };

  const loadUser = async () => {
    if (isNew || !id) { setLoading(false); return; }
    try {
      const res = await userService.getUserById(id);
      if (res.success) {
        const u = res.data;
        setUserData(u);
        setForm({
          username: u.username || '',
          nome: u.nome || '',
          sobrenome: u.sobrenome || '',
          email: u.email || '',
          telefone: u.telefone || '',
          emailVerificado: !!u.emailVerificado,
          roles: u.roles || ['OPERADOR'],
          ativo: !!u.ativo,
          password: ''
        });
      }
    } catch (e) { console.error(e); }
  };

  const loadLinkedCompanies = async () => {
    if (isNew || !id) { setLinkedCompanies([]); return; }
    try {
      const companies = await companyService.getCompaniesByUser(id);
      setLinkedCompanies(companies || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    const run = async () => {
      await Promise.all([loadRoles(), loadUser()]);
      await loadLinkedCompanies();
      setLoading(false);
    };
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const validate = () => {
    const errs: Record<string,string> = {};
    if (!form.username.trim()) errs.username = 'Nome de usuário é obrigatório';
    if (!form.nome.trim()) errs.nome = 'Nome é obrigatório';
    if (!form.sobrenome.trim()) errs.sobrenome = 'Sobrenome é obrigatório';
    if (!form.email.trim()) errs.email = 'Email é obrigatório';
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Email inválido';
    if (!form.telefone.trim()) errs.telefone = 'Telefone é obrigatório';
    if (isNew && !form.password.trim()) errs.password = 'Senha é obrigatória';
    if (form.roles.length === 0) errs.roles = 'Selecione ao menos um perfil';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    try {
      setSaving(true);
      if (isNew) {
        const payload: CreateUserRequest = {
          username: form.username,
          nome: form.nome,
          sobrenome: form.sobrenome,
          email: form.email,
          telefone: form.telefone,
          emailVerificado: form.emailVerificado,
          password: form.password,
          roles: form.roles
        };
        const res = await userService.createUser(payload);
        if (res.success) {
          alertService.toast('success', 'Usuário criado com sucesso');
          navigate('/usuarios');
        } else throw new Error(res.message);
      } else if (id) {
        const payload: UpdateUserRequest = {
          username: form.username,
          nome: form.nome,
          sobrenome: form.sobrenome,
          email: form.email,
          telefone: form.telefone,
          emailVerificado: form.emailVerificado,
          roles: form.roles,
          ativo: form.ativo,
        };
        const res = await userService.updateUser(id, payload);
        if (res.success) {
          alertService.toast('success', 'Usuário atualizado com sucesso');
          navigate('/usuarios');
        } else throw new Error(res.message);
      }
    } catch (e: any) {
      console.error(e);
      alertService.toast('error', e.message || 'Erro ao salvar usuário');
    } finally { setSaving(false); }
  };

  const handleRoleChange = (role: UserRole) => {
    setForm(prev => ({
      ...prev,
      roles: prev.roles.includes(role) ? prev.roles.filter(r => r !== role) : [...prev.roles, role]
    }));
    if (errors.roles) setErrors(prev => ({ ...prev, roles: '' }));
  };

  const getRoleLabel = (role: UserRole) => ({
    ADMIN: 'Administrador', GERENTE: 'Gerente', VENDEDOR: 'Vendedor', OPERADOR: 'Operador', ESTOQUISTA: 'Estoquista', FINANCEIRO: 'Financeiro', COMPRADOR: 'Comprador'
  } as any)[role] || role;

  const alreadyLinkedIds = useMemo(() => linkedCompanies.map(c => c.id), [linkedCompanies]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div></div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/usuarios')} className="p-2 rounded border hover:bg-gray-50" title="Voltar"><ArrowLeft className="h-5 w-5"/></button>
          <div className="bg-red-100 p-2 rounded-lg">
            <UserIcon className="h-6 w-6 text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{isNew ? 'Novo Usuário' : 'Editar Usuário'}</h1>
            <p className="text-gray-600 text-sm">{isNew ? 'Cadastrar um novo usuário' : `Edição de ${userData?.nome || ''}`}</p>
          </div>
        </div>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
          {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
          <Save className="h-4 w-4"/> Salvar
        </button>
      </div>

      {/* Form */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Dados Básicos */}
          <div className="bg-white rounded-lg shadow-sm border p-6 space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Dados Básicos</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
                <input className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 ${errors.username ? 'border-red-300' : 'border-gray-300'}`} value={form.username} onChange={e => setForm(p => ({...p, username: e.target.value}))} />
                {errors.username && <p className="mt-1 text-sm text-red-600">{errors.username}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 ${errors.nome ? 'border-red-300' : 'border-gray-300'}`} value={form.nome} onChange={e => setForm(p => ({...p, nome: e.target.value}))} />
                {errors.nome && <p className="mt-1 text-sm text-red-600">{errors.nome}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sobrenome *</label>
                <input className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 ${errors.sobrenome ? 'border-red-300' : 'border-gray-300'}`} value={form.sobrenome} onChange={e => setForm(p => ({...p, sobrenome: e.target.value}))} />
                {errors.sobrenome && <p className="mt-1 text-sm text-red-600">{errors.sobrenome}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input type="email" className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 ${errors.email ? 'border-red-300' : 'border-gray-300'}`} value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} />
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefone *</label>
                <input className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 ${errors.telefone ? 'border-red-300' : 'border-gray-300'}`} value={form.telefone} onChange={e => setForm(p => ({...p, telefone: e.target.value}))} />
                {errors.telefone && <p className="mt-1 text-sm text-red-600">{errors.telefone}</p>}
              </div>
            </div>
          </div>

          {/* Acesso */}
          <div className="bg-white rounded-lg shadow-sm border p-6 space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Dados de Acesso</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {isNew ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Senha *</label>
                  <input type="password" className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 ${errors.password ? 'border-red-300' : 'border-gray-300'}`} value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))} placeholder="Digite a senha" />
                  {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
                  <button type="button" onClick={() => setPasswordModalOpen(true)} className="inline-flex items-center gap-2 px-3 py-2 rounded bg-red-600 text-white hover:bg-red-700">
                    Alterar senha
                  </button>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Perfis *</label>
                <div className="space-y-2">
                  {availableRoles.map(role => (
                    <label key={role} className="flex items-center">
                      <input type="checkbox" className="mr-2 h-4 w-4 text-red-600" checked={form.roles.includes(role)} onChange={() => handleRoleChange(role)} />
                      <span className="text-sm text-gray-700">{getRoleLabel(role)}</span>
                    </label>
                  ))}
                </div>
                {errors.roles && <p className="mt-1 text-sm text-red-600">{errors.roles}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Configurações e Empresas */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border p-6 space-y-3">
            <h3 className="text-lg font-medium text-gray-900">Configurações</h3>
            <label className="flex items-center">
              <input type="checkbox" className="h-4 w-4 text-red-600" checked={form.emailVerificado} onChange={e => setForm(p => ({...p, emailVerificado: e.target.checked}))} />
              <span className="ml-2 text-sm text-gray-700">Email verificado</span>
            </label>
            {!isNew && (
              <label className="flex items-center">
                <input type="checkbox" className="h-4 w-4 text-red-600" checked={form.ativo} onChange={e => setForm(p => ({...p, ativo: e.target.checked}))} />
                <span className="ml-2 text-sm text-gray-700">Usuário ativo</span>
              </label>
            )}
          </div>

          {/* Empresas do usuário */}
          {!isNew && (
            <div className="bg-white rounded-lg shadow-sm border p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Empresas vinculadas</h3>
                <button onClick={() => setLinkModalOpen(true)} className="inline-flex items-center gap-2 px-3 py-2 rounded bg-red-600 text-white hover:bg-red-700">
                  <Link2 className="h-4 w-4"/> Vincular empresas
                </button>
              </div>

              {linkedCompanies.length === 0 ? (
                <p className="text-sm text-gray-500">Nenhuma empresa vinculada.</p>
              ) : (
                <ul className="divide-y divide-gray-200 border rounded-md">
                  {linkedCompanies.map(c => (
                    <li key={c.id} className="p-3 flex items-center justify-between">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{c.nomeFantasia}</p>
                        <p className="text-xs text-gray-500 truncate">{c.razaoSocial}</p>
                      </div>
                      <span className={`ml-2 inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${c.ativo ? 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20' : 'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-500/10'}`}>
                        {c.ativo ? 'Ativa' : 'Inativa'}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de vínculo */}
      {!isNew && id && (
        <CompanyLinkModal
          isOpen={linkModalOpen}
          onClose={() => setLinkModalOpen(false)}
          alreadyLinkedIds={alreadyLinkedIds}
          onConfirm={async (empresaIds) => {
            if (empresaIds.length === 0) return;
            try {
              await userService.linkCompanies(id, empresaIds);
              alertService.toast('success', 'Empresas vinculadas com sucesso');
              await loadLinkedCompanies();
            } catch (e: any) {
              console.error(e);
              alertService.toast('error', e.message || 'Erro ao vincular empresas');
            }
          }}
        />
      )}

      {/* Modal de alteração de senha */}
      {!isNew && id && passwordModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Alterar senha</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nova senha</label>
                <input type="password" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 border-gray-300" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar senha</label>
                <input type="password" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 border-gray-300" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button className="px-4 py-2 rounded border" onClick={() => { setPasswordModalOpen(false); setNewPassword(''); setConfirmPassword(''); }} disabled={changingPassword}>Cancelar</button>
              <button className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50" disabled={changingPassword || !newPassword || newPassword !== confirmPassword} onClick={async () => {
                if (!id) return;
                if (!newPassword || newPassword !== confirmPassword) return;
                try {
                  setChangingPassword(true);
                  await userService.updatePassword(id, newPassword);
                  alertService.toast('success', 'Senha alterada com sucesso');
                  setPasswordModalOpen(false);
                  setNewPassword('');
                  setConfirmPassword('');
                } catch (e: any) {
                  console.error(e);
                  alertService.toast('error', e.message || 'Erro ao alterar senha');
                } finally { setChangingPassword(false); }
              }}>Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserInfoPage;